import { Component, OnInit, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { marked } from 'marked';
import { TranslateModule } from '@ngx-translate/core';
import { RouterModule } from '@angular/router';
import { InfoProductDto, InfoProductsService, ApplicationType, InfoProductVersionDto } from '../../services/info-products.service';
import { HttpService } from '../../services/http.service';
import { ButtonComponent } from '@libs/front/components/button/button.component';
import { PopupComponent } from '@libs/front/components/popup/popup.component';
import { DeviceService } from '../../services/device.service';

export interface ProductVersion {
  version: string;
  date: string;
  changelog: string | null;
  /** Installer of this exact version, null for the products that do not publish one. */
  installerUrl: string | null;
}

export interface Product {
  id: string;
  application: ApplicationType;
  tagline: string;
  features: string[];
  installerUrl: string;
  versions: ProductVersion[];
}

interface ProductMetadata {
  tagline: string;
  installerUrl: string;
}

interface SelectedChangelog {
  application: ApplicationType;
  version: string;
  date: string;
  changelog: string;
}

/**
 * S3 documentation keys per product. Only products with a key here have a
 * working "Open documentation" button — for the others the button is disabled
 * until their manual is published. Explore is the only one available today.
 */
const DOCUMENTATION_S3_KEYS: Partial<Record<ApplicationType, string>> = {
  Explore: 'Documentation/Explore_User_Manual_v2.html',
  Expertab: 'Documentation/ExperTab_User_Manual.html',
  Expertir: 'Documentation/Expertir_User_Manual.html',
};

const PRODUCT_METADATA: Partial<Record<ApplicationType, ProductMetadata>> = {
  Explore: {
    tagline: 'Turn data into decisions. Drive performance.\nStay in control of your site.',
    installerUrl: '#',
  },
  Expertab: {
    tagline: 'Capture the right data. Ensure quality execution.\nBuild reliable operations.',
    installerUrl: 'https://play.google.com/store/apps/details?id=epc.diogen.expertab&pli=1',
  },
  Expertir: {
    tagline: 'Design smarter blasts. Improve performance.\nUnlock full operational efficiency.',
    installerUrl: '#',
  },
};

@Component({
  selector: 'app-our-products',
  standalone: true,
  imports: [CommonModule, TranslateModule, ButtonComponent, PopupComponent, RouterModule],
  templateUrl: './our-products.component.html',
  styleUrls: ['./our-products.component.scss'],
})
export class OurProductsComponent implements OnInit {

  readonly products = signal<Product[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly supportEmail = signal('support.diogen@epc-groupe.com');

  readonly changelogPopupOpen = signal(false);
  readonly selectedChangelog = signal<SelectedChangelog | null>(null);

  /**
   * Changelogs are rendered as markdown whatever their origin: one written in plain text
   * has no syntax to interpret and comes out as it stands, line breaks included. Angular
   * sanitizes the result of the [innerHTML] binding.
   */
  readonly selectedChangelogHtml = computed(() => {
    const changelog = this.selectedChangelog()?.changelog;
    return changelog ? marked.parse(changelog, { async: false, breaks: true }) : '';
  });

  private readonly deviceService = inject(DeviceService);
  private readonly http = inject(HttpService);
  readonly isMobile = this.deviceService.isMobile;

  constructor(private readonly infoProductsService: InfoProductsService) {}

  ngOnInit(): void {
    this.loading.set(true);
    this.infoProductsService.getAll().subscribe({
      next: (items) => {
        this.products.set(this.buildProducts(items));
        this.loading.set(false);
      },
      error: () => {
        this.error.set('Failed to load products');
        this.loading.set(false);
      },
    });

    this.http.get<string>('Config/EmailAdministrateur').subscribe({
      next: (email) => this.supportEmail.set((email ?? '').replace(/^"|"$/g, '').trim()),
    });
  }

  private buildProducts(items: InfoProductDto[]): Product[] {
    const products: Product[] = items.map((item) => {
      const metadata = PRODUCT_METADATA[item.application] ?? {
        tagline: '',
        installerUrl: '#',
      };

      const versions: ProductVersion[] = (item.details?.versions ?? []).map((v) => ({
        version: v.numero,
        date: new Date(v.date).toLocaleDateString(),
        changelog: this.readChangelog(v),
        installerUrl: v.setupUrl ?? null,
      }));

      return {
        id: item.application.toLowerCase(),
        application: item.application,
        tagline: metadata.tagline,
        features: item.details?.description ?? [],
        installerUrl: metadata.installerUrl,
        versions,
      };
    });

    const order: Partial<Record<ApplicationType, number>> = { Explore: 0, Expertab: 1, Expertir: 2 };
    return products.sort((a, b) => (order[a.application] ?? 99) - (order[b.application] ?? 99));
  }

  /**
   * A published changelog is displayed as it stands. Only the legacy database format,
   * a single ';' separated line, is turned into one line per entry.
   */
  private readChangelog(version: InfoProductVersionDto): string | null {
    if (!version.changelog) return null;
    if (version.changelogFormat === 'raw') return version.changelog;

    return version.changelog
      .replace(/\*/g, '-')
      .split(';')
      .map((entry: string) => entry.trim())
      .filter((entry: string) => entry.length > 0)
      .join('\n');
  }

  getLeftFeatures(features: string[]): string[] {
    return features.slice(0, Math.ceil(features.length / 2));
  }

  getRightFeatures(features: string[]): string[] {
    return features.slice(Math.ceil(features.length / 2));
  }

  // ── Documentation ─────────────────────────────────────────────────────────

  /** Whether the product has a published documentation manual. */
  hasDocumentation(product: Product): boolean {
    return !!DOCUMENTATION_S3_KEYS[product.application];
  }

  /** Opens the global Explore documentation (top "Documentation" card button). */
  openDocumentation(): void {
    this.openDocByKey(DOCUMENTATION_S3_KEYS.Explore);
  }

  /** Opens the documentation manual for a specific product in a new tab. */
  openProductDocumentation(product: Product): void {
    this.openDocByKey(DOCUMENTATION_S3_KEYS[product.application]);
  }

  private openDocByKey(s3Key: string | undefined): void {
    if (!s3Key) return;
    const newTab = window.open('', '_blank');
    this.infoProductsService.getDocumentationUrl(s3Key).subscribe({
      next: ({ url }) => {
        if (newTab) {
          newTab.location.href = url;
        } else {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
      },
      error: () => {
        newTab?.close();
        this.error.set('Failed to open documentation');
      },
    });
  }

  /** Opens the user's mail client to contact support. */
  sendSupportMail(): void {
    window.location.href = `mailto:${this.supportEmail()}`;
  }

  // ── Installer download ──────────────────────────────────────────────────────

  /** Installer of the most recent version, falling back to the product store page. */
  private latestInstallerUrl(product: Product): string | null {
    const published = product.versions.find((v) => v.installerUrl)?.installerUrl;
    const fallback = product.installerUrl && product.installerUrl !== '#' ? product.installerUrl : null;
    return published ?? fallback;
  }

  hasInstaller(product: Product): boolean {
    return !!this.latestInstallerUrl(product);
  }

  downloadProduct(product: Product): void {
    const url = this.latestInstallerUrl(product);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }

  /** Whether the version row has something to download, its installer or its changelog. */
  canDownloadVersion(version: ProductVersion): boolean {
    return !!version.installerUrl || !!version.changelog;
  }

  /** Downloads the installer of a version, or its changelog for the products without one. */
  downloadVersion(product: Product, version: ProductVersion): void {
    if (version.installerUrl) {
      window.open(version.installerUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    this.downloadChangelog(product, version);
  }

  // ── Changelog ──────────────────────────────────────────────────────────────

  /** Opens the changelog popup for a given version. */
  openChangelog(product: Product, version: ProductVersion): void {
    if (!version.changelog) return;
    this.selectedChangelog.set({
      application: product.application,
      version: version.version,
      date: version.date,
      changelog: version.changelog,
    });
    this.changelogPopupOpen.set(true);
  }

  closeChangelog(): void {
    this.changelogPopupOpen.set(false);
  }

  /** Downloads the changelog of a version as a plain-text file. */
  private downloadChangelog(product: Product, version: ProductVersion): void {
    if (!version.changelog) return;
    const content = `${product.application} ${version.version} - ${version.date}\n\n${version.changelog}\n`;
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${product.application}_${version.version}_changelog.txt`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      a.remove();
      window.URL.revokeObjectURL(url);
    }, 100);
  }
}
